import MediaGallery from "./MediaGallery";
import { CircularProgress, Dialog, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getToken, requestErrorHandler } from "../Utils/Common";
import EnvSettings from "../Utils/EnvSettings";
import { useStyles } from "../Utils/Styles";
import { showPopupSnackbar } from "../redux/actions/snackbarActions";
import Title from "./Title";

const server = EnvSettings.server;

/**
 *
 * @param {*} resource { type, number }
 * @param {*} listCallback
 * @returns
 */
export default function MediaFiles({ resource, listCallback }) {
  // Endpoints get, upload, delete
  // Resource { type: , number: }

  const classes = useStyles();
  const [checklistURL, setChecklistURL] = useState();
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const listRequest = useRef();
  const [files, setFiles] = useState();
  const [fileToDelete, setFileToDelete] = useState();
  const { t } = useTranslation();

  const fileUploadRef = useRef();

  const dispatch = useDispatch();

  const getPhotos = () => {
    if (listRequest.current) listRequest.current.abort();
    const controller = new AbortController();
    listRequest.current = controller;
    setLoading(true);
    setListError(false);
    setChecklistURL();
    const requestOptions = {
      method: "POST",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        resource: resource,
      }),
    };
    fetch(server + "/get_media_files", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        if (controller.signal.aborted) return;
        setListError(!!result.error);
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          if (result.files) {
            setChecklistURL(result);
            if (listCallback) {
              listCallback(result);
            }
          }
        }
      }).catch(() => {
        if (!controller.signal.aborted) setListError(true);
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  };

  useEffect(() => {
    if (!resource || !resource.number) {
      return;
    }
    getPhotos();
    return () => { if (listRequest.current) listRequest.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource?.type, resource?.number, resource?.refresh, dispatch]);

  const uploadPhoto = (e) => {
    if (!files || !files.length) {
      return;
    }
    const data = new FormData();
    data.append("token", getToken());
    files.forEach((element) => {
      data.append("files[]", element);
    });
    data.append("resource", JSON.stringify(resource));
    fetch(server + "/upload_media_files", {
      method: "POST",
      body: data,
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        dispatch(showPopupSnackbar(result));
        if (!result.error) {
          getPhotos();
          fileUploadRef.current.value = null;
          setFiles([]);
        }
      });
  };

  const removePhoto = () => {
    if (!fileToDelete || !resource?.number) {
      return;
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        filename: fileToDelete,
        resource: resource,
      }),
    };
    fetch(server + "/delete_media_file", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          dispatch(showPopupSnackbar(result));
        }
        getPhotos();
      });
  };

  const handleClose = () => {
    setFileToDelete(undefined);
  };

  return resource && resource.number ? (
    <Grid container spacing={3}>
      <Dialog
        open={fileToDelete ? true : false}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("delete")} {fileToDelete}?
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              removePhoto();
              handleClose();
            }}
            color="primary"
            autoFocus
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container spacing={3} item xs={12}>
        <Grid item xs={12}>
          <Title>{t("media")}</Title>
        </Grid>
        {loading ? (
          <Grid item xs={12}><CircularProgress size={24} aria-label={t("mediaPreview.loading")} /></Grid>
        ) : listError ? (
          <Grid item xs={12}>
            <Typography color="error" role="alert">{t("mediaPreview.listFailed")}</Typography>
            <Button onClick={getPhotos}>{t("mediaPreview.retry")}</Button>
          </Grid>
        ) : checklistURL && checklistURL.attachments?.length ? (
          <Grid item xs={12}>
            <MediaGallery attachments={checklistURL.attachments}
              resource={resource} onDelete={setFileToDelete} />
          </Grid>
        ) : (
          <Grid item><Typography>{t("noMediaAvailable")}</Typography></Grid>
        )}
      </Grid>
      <Grid container item xs={12} sm={6}>
        <Grid item xs={12}>
          <Title>{t("uploadNewMedia")}</Title>
        </Grid>
        <Grid item>
          <input
            accept="image/*,.pdf"
            className={classes.input}
            id="contained-button-file"
            type="file"
            multiple
            onChange={(e) => {
              if (e.target.files) {
                setFiles(Array.prototype.slice.call(e.target.files));
              }
            }}
            ref={fileUploadRef}
          />
        </Grid>
        <Grid item>
          <label htmlFor="contained-button-file">
            <Button
              variant="outlined"
              disabled={!(files && files.length) ? true : false}
              onClick={uploadPhoto}
            >
              {t("uploadMedia")}
            </Button>
          </label>
        </Grid>
      </Grid>
    </Grid>
  ) : (
    <Typography>{t("ticketList.submitTicketFirst")}!</Typography>
  );
}
